import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DataTable from '../components/ResponsiveDataTable';
import { API_BASE_URL, apiCall } from '../config/api';

const PricingHistory = () => {
  const [pricingData, setPricingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  
  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // History filters state
  const [filters, setFilters] = useState({
    hub_id: '',
    snapshot_time: getTodayDate(),
  });

  // Frontend pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Frontend search state
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadWarehouses();
    // Load pricing history on component mount with today's date
    loadPricingHistory();
  }, []);

  useEffect(() => {
    loadPricingHistory();
  }, [filters.hub_id, filters.snapshot_time]);

  // Frontend search and pagination effect
  useEffect(() => {
    let filtered = pricingData;
    
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = pricingData.filter(item => 
        item.sku_code?.toLowerCase().includes(term) ||
        item.product_name?.toLowerCase().includes(term)
      );
    }
    
    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchTerm, pricingData]);

  const loadWarehouses = async () => {
    try {
      const response = await apiCall(`${API_BASE_URL}/listings/getWarehouseList`);
      if (response && response.data) {
        setWarehouses(response.data);
      }
    } catch (error) {
      console.error('Error loading warehouses:', error);
      toast.error('Failed to load warehouses');
    }
  };

  const loadPricingHistory = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.hub_id) queryParams.append('hub_id', filters.hub_id);
      if (filters.snapshot_time) queryParams.append('snapshot_time', filters.snapshot_time);

      const url = `${API_BASE_URL}/pricing-history?${queryParams}`;
      const response = await apiCall(url);

      if (response && response.success) {
        setPricingData(response.data || []);
        setFilteredData(response.data || []);
        setCurrentPage(1); // Reset to first page when data loads
      }
    } catch (error) {
      console.error('Error loading pricing history:', error);
      toast.error('Failed to load pricing history');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const handleSearch = () => {
    loadPricingHistory();
  };

  const handleClearFilters = () => {
    setFilters({
      hub_id: '',
      snapshot_time: getTodayDate(),
    });
    setSearchTerm('');
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPageSize(newPerPage);
    setCurrentPage(1);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatPriceRange = (slab, index, slabs) => {
    const startQty = slab.qty;
    const endQty = slab.max || '∞';
    
    if (index === slabs.length - 1 && !slab.max) {
      return `${startQty}+ units`;
    }
    
    return `${startQty}-${endQty} units`;
  };

  const columns = [
    {
      name: 'Snapshot Time',
      selector: (row) => row.snapshot_time,
      sortable: true,
      cell: (row) => <div>{formatDateTime(row.snapshot_time)}</div>,
      width: '180px',
    },
    {
      name: 'Hub',
      selector: (row) => row.warehouse?.name,
      sortable: true,
      cell: (row) => (
        <div>
          <div style={{ fontWeight: '600' }}>{row.warehouse?.name}</div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>ID: {row.hub_id}</div>
        </div>
      ),
      width: '150px',
    },
    {
      name: 'SKU Code',
      selector: (row) => row.sku_code,
      sortable: true,
      cell: (row) => (
        <div style={{ fontWeight: '600', color: '#0d6efd' }}>{row.sku_code}</div>
      ),
      width: '120px',
    },
    {
      name: 'Product Name',
      selector: (row) => row.product_name,
      sortable: true,
      cell: (row) => (
        <div>
          <div style={{ fontWeight: '600' }}>{row.product_name}</div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>ID: {row.product_id}</div>
        </div>
      ),
      width: '200px',
    },
    {
      name: 'Discount Price',
      selector: (row) => {
        if (Array.isArray(row.discount_price) && row.discount_price.length > 0) {
          return row.discount_price[0].price;
        }
        return 0;
      },
      sortable: true,
      cell: (row) => (
        <div className="price-slabs-container">
          {Array.isArray(row.discount_price) && row.discount_price.length > 0 ? (
            row.discount_price.map((slab, index) => (
              <div key={index} className="price-slab-item" style={{ marginBottom: '8px' }}>
                <div className="slab-range" style={{ fontSize: '12px', color: '#6c757d' }}>
                  {formatPriceRange(slab, index, row.discount_price)}
                </div>
                <div className="slab-price">
                  <span className="price-display" style={{ fontWeight: '600', color: '#28a745' }}>
                    {formatPrice(slab.price)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <span style={{ color: '#6c757d' }}>No pricing</span>
          )}
        </div>
      ),
      width: '200px',
    },
    {
      name: 'Status',
      selector: (row) => row.active,
      sortable: true,
      cell: (row) => (
        <span
          className={row.active ? 'status-badge status-confirmed' : 'status-badge status-cancelled'}
          style={{
            padding: '6px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {row.active ? 'Active' : 'Inactive'}
        </span>
      ),
      width: '100px',
      center: true,
    },
  ];

  const subHeaderComponent = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        padding: '12px 0',
        gap: '12px',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: '150px' }}>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#495057' }}>
          Hub *
        </label>
        <select
          name="hub_id"
          value={filters.hub_id}
          onChange={handleFilterChange}
          style={{
            padding: '10px 12px',
            border: '2px solid #e2e8f0',
            borderRadius: '6px',
            fontSize: '14px',
            width: '100%',
            backgroundColor: 'white',
          }}
        >
          <option value="">All Hubs</option>
          {warehouses.map((warehouse) => (
            <option key={warehouse.id} value={warehouse.id}>
              {warehouse.name}
            </option>
          ))}
        </select>
      </div>

      <div style={{ minWidth: '150px' }}>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#495057' }}>
          Date * (Required)
        </label>
        <input
          type="date"
          name="snapshot_time"
          value={filters.snapshot_time}
          onChange={handleFilterChange}
          style={{
            padding: '10px 12px',
            border: '2px solid #e2e8f0',
            borderRadius: '6px',
            fontSize: '14px',
            width: '100%',
          }}
        />
      </div>

      <div style={{ minWidth: '200px' }}>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#495057' }}>
          Search by SKU or Product
        </label>
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: '10px 12px',
            border: '2px solid #e2e8f0',
            borderRadius: '6px',
            fontSize: '14px',
            width: '100%',
          }}
        />
      </div>

      <button
        onClick={handleSearch}
        style={{
          padding: '10px 16px',
          border: 'none',
          borderRadius: '6px',
          fontSize: '14px',
          backgroundColor: '#0d6efd',
          color: 'white',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          marginTop: '20px',
        }}
        onMouseOver={(e) => {
          e.target.style.backgroundColor = '#0b5ed7';
        }}
        onMouseOut={(e) => {
          e.target.style.backgroundColor = '#0d6efd';
        }}
      >
        Search
      </button>

      {(filters.hub_id || searchTerm) && (
        <button
          onClick={handleClearFilters}
          style={{
            padding: '10px 16px',
            border: '1px solid #6c757d',
            borderRadius: '6px',
            fontSize: '14px',
            backgroundColor: '#6c757d',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginTop: '20px',
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#5c636a';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#6c757d';
          }}
        >
          Clear Filters
        </button>
      )}
    </div>
  );

  const customStyles = {
    header: {
      style: {
        minHeight: '56px',
        paddingLeft: '0px',
        paddingRight: '0px',
      },
    },
    headRow: {
      style: {
        borderTopStyle: 'solid',
        borderTopWidth: '1px',
        borderTopColor: '#dee2e6',
        backgroundColor: '#f8f9fa',
      },
    },
    headCells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#dee2e6',
        },
        backgroundColor: '#f8f9fa',
        fontWeight: '600',
        fontSize: '14px',
        color: '#495057',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '16px',
      },
    },
    cells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#dee2e6',
        },
        padding: '16px',
        fontSize: '14px',
      },
    },
    rows: {
      style: {
        '&:hover': {
          backgroundColor: '#f8f9fa',
          cursor: 'pointer',
        },
        borderBottomColor: '#dee2e6',
      },
    },
    pagination: {
      style: {
        borderTop: '1px solid #dee2e6',
        padding: '16px',
      },
    },
  };

  return (
    <div className="container">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Pricing History</h1>
          {/* <p className="page-subtitle">
            View daily pricing snapshots captured at 12:00 PM and 5:00 PM.
          </p> */}
        </div>
      </div>

      {/* Info Card */}
      {/* <div
        className="card"
        style={{
          marginBottom: '20px',
          backgroundColor: '#e7f3ff',
          borderLeft: '4px solid #0d6efd',
        }}
      >
        <div style={{ padding: '16px' }}>
          <div style={{ fontWeight: '600', color: '#0d6efd', marginBottom: '8px' }}>
            📊 Pricing Snapshots
          </div>
          <div style={{ fontSize: '14px', color: '#495057' }}>
            Pricing data is captured twice daily at 12:00 PM and 5:00 PM IST. Select a date and hub to view all pricing snapshots for that day. Use the search box to find specific products by SKU code or product name.
          </div>
        </div>
      </div> */}

      {/* Data Table Card */}
      <div className="card">
        <DataTable
          columns={columns}
          data={filteredData}
          progressPending={loading}
          pagination
          paginationTotalRows={filteredData.length}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          paginationDefaultPage={currentPage}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          subHeader
          subHeaderComponent={subHeaderComponent}
          selectableRows={false}
          customStyles={customStyles}
          highlightOnHover
          pointerOnHover
          responsive
          striped
          noDataComponent={
            <div
              style={{
                padding: '60px 40px',
                textAlign: 'center',
                color: '#6c757d',
                fontSize: '16px',
              }}
            >
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                No pricing history found
              </div>
              <div>Try adjusting your search criteria or filters</div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default PricingHistory;
